import React, { useEffect, useState } from 'react';

import { validateIntentName } from '@paskills/validation';
import { ApiError, granetApi } from 'client/api';
import { GrammarPageIntentSettings } from 'client/blocks/grammar/GrammarPageIntentSettings/GrammarPageIntentSettings';
import { useGrammarPageContext } from 'client/context/grammarPageContext';
import { useSettingsPageContext } from 'client/context/settingsPageContext';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { useFormSettings } from 'client/hooks/useFormSettings';
import {
    GrammarIntentEditableFields,
    GrammarIntentShort,
    GrammarIntentTestResult,
    GrammarValidationErrorPayload,
} from 'client/model/grammar';

interface Props {
    intent: GrammarIntentShort;
    onDelete: () => void;
    readOnly?: boolean;
    onClose: () => void;
    customEntitiesNames: string[];
    isChanged: boolean;
    setIsChanged: (isChanged: boolean) => void;
    intentSettings: GrammarIntentEditableFields;
}

export const GrammarPageIntentModule: React.FC<Props> = ({
    intent,
    onDelete,
    readOnly,
    onClose,
    customEntitiesNames,
    isChanged,
    setIsChanged,
    intentSettings,
}) => {
    const [validationError, setValidationError] = useState<GrammarValidationErrorPayload | null>(null);

    const skill = useCurrentAliceApp()!;
    const [testResults, setTestResults] = useState<GrammarIntentTestResult>();
    const { isEditable } = useSettingsPageContext();
    const { settings, onChange, errors, hasErrors } = useFormSettings({
        defaultSettings: intentSettings,
        readOnly: !isEditable,
        registerChanges: setIsChanged,
        validators: {
            formName: (value) => value && validateIntentName(value),
        },
    });

    const { refreshIntents } = useGrammarPageContext();

    const onSave = async () => {
        setValidationError(null);

        const { validationError } = await granetApi.updateGrammarIntent({
            skillId: skill.id,
            channel: skill.channel,
            intentId: intent.id,
            intentSettings: settings,
        });

        setValidationError(validationError);

        await refreshIntents();

        setIsChanged(false);
    };
    const [onSaveHandler, saveState, initSaveState] = useFetchingHandler({
        throwErrors: true,
        handler: onSave,
    });
    const [onTestHandler, testState, initTestState] = useFetchingHandler({
        throwErrors: true,
        omitResult: true,
        handler: async () => {
            if (isChanged) {
                await onSave();
            }

            return granetApi.runGrammarIntentTests({ skillId: skill.id, intentId: intent.id }, skill.channel);
        },
        onLoad: (res) => {
            setTestResults(res);
        },
        onError: (e) => {
            if (e instanceof ApiError && e.isGrammarValidationError()) {
                setValidationError(e.payload ?? null);
            }
        },
    });
    const [onDeleteHandler, deleteState, initDeleteState] = useFetchingHandler({
        throwErrors: true,
        handler: async () => {
            await granetApi.deleteGrammarIntent({ skillId: skill.id, intentId: intent.id });
            onDelete();
        },
    });
    useEffect(() => {
        initSaveState();
        initTestState();
        initDeleteState();
    }, [initDeleteState, initSaveState, initTestState]);

    return (
        <GrammarPageIntentSettings
            readOnly={readOnly}
            onClose={onClose}
            customEntitiesNames={customEntitiesNames}
            onTestHandler={onTestHandler}
            onDeleteHandler={onDeleteHandler}
            onSaveHandler={onSaveHandler}
            saveState={saveState}
            testState={testState}
            deleteState={deleteState}
            setValidationError={setValidationError}
            validationError={validationError}
            testResults={testResults}
            onChange={onChange}
            hasErrors={hasErrors}
            errors={errors}
            settings={settings}
            intent={intent}
        />
    );
};
